<template>
    <div>
        <div class="row" v-if="forms.length">
            <div class="col">
                {{ translate('select_form') }}:
                <select v-model="selectedForm" :disabled="shouldDisableActions">
                    <option v-for="form in forms" :key="form">
                        {{ form }}
                    </option>
                </select>
            </div>
        </div>
        <div v-else>
            {{ translate('dag_investment_form_submits_no_forms') }}
        </div>

        <div class="mt-5" v-if="formSubmits.length">
            <label :class="{ 'disabled': shouldDisableActions }">
                <input type="checkbox" v-model="useFixedTableHeight" @change="updateTableHeight" @keydown.tab.prevent :disabled="shouldDisableActions">
                {{ translate('use_fixed_table_height') }}
            </label>
        </div>

        <div class="mt-2" v-if="formSubmits.length">
            <div class="actions d-flex">
                <div>
                    <label :class="{ 'disabled': shouldDisableActions }">
                        <input type="checkbox" :checked="hasMarkedAllSubmits" @change="toggleAllSubmits" @keydown.tab.prevent :disabled="shouldDisableActions">
                        {{ translate('mark_all') }}
                    </label>
                </div>
                <div class="ml-4" v-if="markedSubmits.length">
                    <a class="btn btn-primary" :href="csvDownloadUrl" :class="{ 'disabled': shouldDisableActions }">
                        {{ translate('export_csv') }}
                    </a>
                    <button class="btn btn-danger" @click="deleteFormSubmits" :disabled="shouldDisableActions">
                        {{ translate('delete') }}
                    </button>
                </div>
            </div>
            <div class="table-wrap mt-2" ref="tableWrap">
                <table class="table table-bordered">
                    <tr>
                        <th>
                            {{ translate('mark') }}
                        </th>
                        <th v-for="(column, i) in formSubmits[0].data" :key="i">
                            {{ column[0] }}
                        </th>
                    </tr>
                    <tr v-for="(submit, i) in formSubmits" :key="i" :class="{ 'is-marked': markedSubmits.includes(i) }">
                        <td class="text-center">
                            <input type="checkbox" :value="i" v-model="markedSubmits" @keydown.tab.prevent :disabled="shouldDisableActions">
                        </td>
                        <td v-for="(column, j) in submit.data" :key="j" :class="{ 'is-editable': !shouldDisableActions }">
                            <input type="text" ref="editedField" v-if="isEditingField(i, j)" v-model="formSubmits[i].data[j][1]" @keyup.enter="stopEditingAndUpdate" @keyup.escape="stopEditingAndCancel" @keydown.tab.prevent="navigateToNextCell($event, i, j)" v-click-outside="stopEditingAndUpdate">
                            <span v-else>
                                {{ column[1] }}
                                <span class="edit-field" @click.stop="editField(i, j)" v-if="!shouldDisableActions">
                                    <span class="sr-only">{{ translate('edit') }}</span>
                                </span>
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ translate('sum') }}</th>
                        <td v-for="i in numberOfColumns" :key="i">
                            <strong v-if="sum = sumToDisplay(i - 1)">
                                {{ sum | money }}
                            </strong>
                        </td>
                    </tr>
                </table>
            </div>
            <div class="actions d-flex mt-2">
                <div>
                    <label :class="{ 'disabled': shouldDisableActions }">
                        <input type="checkbox" :checked="hasMarkedAllSubmits" @change="toggleAllSubmits" @keydown.tab.prevent :disabled="shouldDisableActions">
                        {{ translate('mark_all') }}
                    </label>
                </div>
                <div class="ml-4" v-if="markedSubmits.length">
                    <a class="btn btn-primary" :href="csvDownloadUrl" :class="{ 'disabled': shouldDisableActions }">
                        {{ translate('export_csv') }}
                    </a>
                    <button class="btn btn-danger" @click="deleteFormSubmits" :disabled="shouldDisableActions">
                        {{ translate('delete') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

let controller;

export default {
    props: {
        initialForms: {
            type: Array,
            required: true,
            default: []
        },
        summableFields: {
            type: Array,
            required: true,
            default: []
        }
    },

    data () {
        return {
            forms: [],
            selectedForm: null,
            formSubmits: [],
            markedSubmits: [],
            editedField: null,
            oldFieldValue: null,
            isFetchingFormSubmits: false,
            isUpdatingOrDeletingFormSubmits: false,
            useFixedTableHeight: false,
            tableHeight: 'auto'
        }
    },

    computed: {
        isEditingAnyField () {
            return this.editedField !== null;
        },

        shouldDisableActions () {
            return this.isEditingAnyField || this.isUpdatingOrDeletingFormSubmits;
        },

        numberOfColumns () {
            return this.formSubmits.length > 0
                ? this.formSubmits[0].data.length
                : 0;
        },

        hasMarkedAllSubmits () {
            return this.markedSubmits.length === this.formSubmits.length;
        },

        fieldSums () {
            const sums = {};

            for (const field of this.summableFields) {
                sums[field] = {
                    position: null,
                    value: 0
                };
            }

            for (const submit of this.formSubmits) {
                let position = 0;

                for (const [field, value] of submit.data) {
                    if (this.summableFields.includes(field)) {
                        const standardizedValue = value.replace(/\./g, '').replace(',', '.');
                        sums[field].value += parseFloat(standardizedValue || 0);

                        if (!sums[field].position) {
                            sums[field].position = position;
                        }
                    }

                    position++;
                }
            }

            return sums;
        },

        csvDownloadUrl () {
            const submitIds = [];

            for (const i of this.markedSubmits) {
                submitIds.push(this.formSubmits[i].id);
            }

            return `/dagoadmin/export-investment-form-submit-csv?submits=${submitIds.join(',')}`;
        }
    },

    created () {
        this.forms = this.initialForms;
        this.selectedForm = this.forms[0];

        document.addEventListener('keydown', this.handleGlobalKeydown);
    },

    mounted () {
        this.updateTableHeight();
        window.addEventListener('resize', this.updateTableHeight);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleGlobalKeydown);
        window.removeEventListener('resize', this.updateTableHeight);
    },

    watch: {
        selectedForm (selectedForm) {
            this.fetchFormSubmits(selectedForm);
        }
    },

    methods: {
        fetchFormSubmits (form) {
            const apiUrl = `/dagoadmin/get-dag-investment-form-submits?formName=${form}`;

            controller = new AbortController();

            this.formSubmits = [];
            this.markedSubmits = [];
            this.isFetchingFormSubmits = true;

            axios.get(apiUrl, { signal: controller.signal })
                .then(response => {
                    for (const formSubmit of response.data) {
                        this.formSubmits.push({
                            id: formSubmit.id,
                            data: Object.entries(JSON.parse(formSubmit.data))
                        });
                    }

                    this.$nextTick(() => {
                        this.updateTableHeight();
                    });

                    // If there are no form submits (e.g., because we just
                    // delete the last one) we remove it from the list of
                    // selectable forms
                    if (!this.formSubmits.length) {
                        this.forms = this.forms.filter(form => form !== this.selectedForm);
                        this.selectedForm = null;
                    }
                })
                .catch(err => {
                    if (!axios.isCancel(err)) {
                        console.error(err);
                    }
                })
                .finally(() => {
                    this.isFetchingFormSubmits = false;
                });
        },

        updateTableHeight() {
            if (!this.$refs.tableWrap) {
                return;
            }

            if (!this.useFixedTableHeight) {
                this.tableHeight = 'auto';
                return;
            }

            const tableWrap = this.$refs.tableWrap;
            const rect = tableWrap.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const padding = 50; // Some bottom spacing to keep the actions in view

            this.tableHeight = `${viewportHeight - rect.top - padding}px`;
        },

        handleGlobalKeydown (event) {
            if (event.key !== 'Tab') {
                return;
            }

            // Always prevent default Tab behavior to disallow selecting
            // checkboxes
            event.preventDefault();

            if (!this.formSubmits.length || this.shouldDisableActions) {
                return;
            }

            if (event.shiftKey) {
                // Go to last cell
                this.editField(
                    this.formSubmits.length - 1,
                    this.formSubmits[0].data.length - 1
                );

                return;
            }

            // Go to first cell
            this.editField(0, 0);
        },

        navigateToNextCell(event, currentRow, currentCol) {
            const isShiftPressed = event.shiftKey;
            const totalRows = this.formSubmits.length;
            const totalCols = this.formSubmits[0].data.length;

            let nextRow = currentRow;
            let nextCol = currentCol + (isShiftPressed ? -1 : 1);

            // Handle column overflow/underflow
            if (nextCol >= totalCols) {
                nextRow++;
                nextCol = 0;
            } else if (nextCol < 0) {
                nextRow--;
                nextCol = totalCols - 1;
            }

            // Handle row overflow/underflow
            if (nextRow >= totalRows) {
                nextRow = 0;
                nextCol = 0;
            } else if (nextRow < 0) {
                nextRow = totalRows - 1;
                nextCol = totalCols - 1;
            }

            this.stopEditingAndUpdate();
            this.editField(nextRow, nextCol);
        },

        editField (i, j) {
            if (this.shouldDisableActions) {
                return;
            }

            this.oldFieldValue = this.formSubmits[i].data[j][1];
            this.editedField = [i, j];

            this.$nextTick(() => {
                if (this.$refs.editedField && this.$refs.editedField[0]) {
                    this.$refs.editedField[0].focus();
                }
            });
        },

        isEditingField (i, j) {
            return this.isEditingAnyField &&
                this.editedField[0] === i &&
                this.editedField[1] === j;
        },

        stopEditingAndUpdate () {
            const formSubmitIndex = this.editedField[0];
            const oldFieldValue = this.oldFieldValue;
            const editedFieldValue = this.formSubmits[this.editedField[0]].data[this.editedField[1]][1];

            this.oldFieldValue = null;
            this.editedField = null;

            if (oldFieldValue !== editedFieldValue) {
                this.updateFormSubmit(this.formSubmits[formSubmitIndex]);
            }
        },

        stopEditingAndCancel () {
            this.formSubmits[this.editedField[0]].data[this.editedField[1]][1] = this.oldFieldValue;

            this.oldFieldValue = null;
            this.editedField = null;
        },

        toggleAllSubmits (event) {
            if (event.target.checked) {
                this.markedSubmits = [...this.formSubmits.keys()];
                return
            }

            this.markedSubmits = [];
        },

        sumToDisplay (position) {
            const found = Object.values(this.fieldSums).find(
                sum => sum.position === position
            );

            return found ? found.value : null;
        },

        updateFormSubmit (formSubmit) {
            const apiUrl = '/dagoadmin/update-dag-investment-form-submit';

            controller = new AbortController();

            this.isUpdatingOrDeletingFormSubmits = true;

            axios.post(apiUrl, {
                id: formSubmit.id,
                data: formSubmit.data
            }, { signal: controller.signal })
                .then(response => {
                    // Nothing to do?
                })
                .catch(err => {
                    if (!axios.isCancel(err)) {
                        console.error(err);
                        window.alert(this.translate('dag_investment_form_submits_update_error'));
                    }
                })
                .finally(() => {
                    this.isUpdatingOrDeletingFormSubmits = false;
                });
        },

        deleteFormSubmits () {
            if (!window.confirm(this.translate('dag_investment_form_submits_confirm_delete'))) {
                return;
            }

            const submitIds = [];

            for (const i of this.markedSubmits) {
                submitIds.push(this.formSubmits[i].id);
            }

            const apiUrl = '/dagoadmin/delete-dag-investment-form-submits';

            controller = new AbortController();

            this.isUpdatingOrDeletingFormSubmits = true;

            axios.post(apiUrl, {
                ids: submitIds
            }, { signal: controller.signal })
                .then(response => {
                    // We re-fetch form submits to update the
                    this.fetchFormSubmits(this.selectedForm);
                })
                .catch(err => {
                    if (!axios.isCancel(err)) {
                        console.error(err);
                        window.alert(this.translate('dag_investment_form_submits_delete_error'));
                    }
                })
                .finally(() => {
                    this.isUpdatingOrDeletingFormSubmits = false;
                });
        },
    },

    filters: {
        money(value) {
            return new Intl.NumberFormat(
                'de-DE',
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
            ).format(value);
        }
    }
}
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';

label {
    cursor: pointer;

    &.disabled {
        cursor: initial;
        pointer-events: none;
    }
}

.actions {
    line-height: 38px; // Matches the button height
}

.table-wrap {
    height: v-bind(tableHeight);
    overflow-y: auto; // We need to allow vertical scrolling since we limit the table height
}

table {
    tr {
        &.is-marked {
            background-color: $almostWhite;
        }

        th,
        td {
            white-space: nowrap;
        }

        td {
            position: relative;
            text-align: left;

            &.is-editable {
                @media (hover: hover) {
                    &:hover {
                        background-color: $lightest;
                    }
                }
            }

            .edit-field {
                cursor: pointer;
                height: 100%;
                left: 0;
                position: absolute;
                top: 0;
                width: 100%;
            }
        }
    }
}
</style>
